import { ReactNode, useEffect, useRef, useMemo, useCallback } from 'react'
import { MapContainer as LeafletMapContainer, TileLayer, MapContainerProps, useMap } from 'react-leaflet'
import { Box, useTheme } from '@mui/material'
import L from 'leaflet'
import './MapContainer.css'

// Fix default marker icon issue with Leaflet
// This affects both regular markers AND leaflet-draw markers
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png'

// Fix L.Icon.Default for leaflet-draw which uses it internally
// Must delete _getIconUrl first to prevent it from using the broken default URL resolver
// @ts-expect-error - _getIconUrl is a private property not in types
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

// Create a properly configured default icon instance
const DefaultIcon = new L.Icon.Default()

// Set as the default icon for all markers
L.Marker.prototype.options.icon = DefaultIcon

interface MapProps extends Partial<MapContainerProps> {
  children?: ReactNode
  height?: string | number
  onViewportChange?: (bounds: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } }) => void
  tileUrl?: string
  tileAttribution?: string
}

// Helper component to track map instance and handle resizing
function MapInstanceTracker({
  onMapReady,
  onViewportChange
}: {
  onMapReady: (map: L.Map) => void
  onViewportChange?: (bounds: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } }) => void
}) {
  const map = useMap()
  const onViewportChangeRef = useRef(onViewportChange)

  // Update the ref whenever the callback changes
  useEffect(() => {
    onViewportChangeRef.current = onViewportChange
  }, [onViewportChange])

  useEffect(() => {
    if (map) {
      onMapReady(map)
    }

    // Cleanup on unmount
    return () => {
      if (map) {
        try {
          // Stop any ongoing animations
          map.stop()
        } catch (e) {
          // Ignore errors
        }
      }
    }
  }, [map, onMapReady])

  // Handle window resize and map invalidation
  useEffect(() => {
    if (!map) return

    const handleResize = () => {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        try {
          map.invalidateSize()
        } catch (e) {
          // Map might be removed, ignore error
        }
      }, 100)
    }

    // Listen for window resize
    window.addEventListener('resize', handleResize)

    // Also set up a ResizeObserver to detect container size changes (e.g., sidebar toggle)
    const container = map.getContainer()
    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(container)

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
    }
  }, [map])

  // Track viewport changes with 1 second debounce
  useEffect(() => {
    if (!map) return

    let debounceTimer: number | null = null

    const handleViewportChange = () => {
      const callback = onViewportChangeRef.current
      if (!callback) return

      const bounds = map.getBounds()
      const northEast = bounds.getNorthEast()
      const southWest = bounds.getSouthWest()

      callback({
        northEast: { lat: northEast.lat, lng: northEast.lng },
        southWest: { lat: southWest.lat, lng: southWest.lng }
      })
    }

    const debouncedViewportChange = () => {
      // Clear any existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      // Wait 1 second after user stops moving
      debounceTimer = setTimeout(() => {
        handleViewportChange()
      }, 1000)
    }

    // Listen for map movement with debouncing
    map.on('moveend', debouncedViewportChange)
    map.on('zoomend', debouncedViewportChange)

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
      map.off('moveend', debouncedViewportChange)
      map.off('zoomend', debouncedViewportChange)
    }
  }, [map])

  return null
}

// Tile configuration
const LIGHT_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const LIGHT_TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'

export function MapContainer({ children, height = '400px', onViewportChange, tileUrl, tileAttribution, ...props }: MapProps) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  // Use a unique key for each map instance
  const mapKey = useMemo(() => `map-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`, [])
  const mapInstanceRef = useRef<L.Map | null>(null)

  const handleMapReady = useCallback((map: L.Map) => {
    mapInstanceRef.current = map
  }, [])

  useEffect(() => {
    // Let react-leaflet handle its own cleanup
    // We only need to clean up our references
    return () => {
      mapInstanceRef.current = null
    }
  }, [])

  // Memoize center to prevent unnecessary re-renders
  const center = useMemo(() => {
    const centerValue = props.center || [40.7128, -74.0060]
    return centerValue as [number, number]
  }, [props.center])

  return (
    <Box
      id={mapKey}
      sx={{
        height,
        width: '100%',
        overflow: 'hidden',
        margin: 0,
        padding: 0,
        // Soften tiles in dark mode
        ...(isDark && {
          '& .leaflet-tile-pane': {
            filter: 'brightness(0.7) contrast(1.1) saturate(0.8)',
          },
        }),
      }}
    >
      <LeafletMapContainer
        key={mapKey}
        center={center}
        zoom={props.zoom || 13}
        minZoom={3}
        maxZoom={19}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        {...props}
      >
        <MapInstanceTracker onMapReady={handleMapReady} onViewportChange={onViewportChange} />
        <TileLayer
          attribution={tileAttribution || LIGHT_TILE_ATTRIBUTION}
          url={tileUrl || LIGHT_TILE_URL}
          maxZoom={19}
        />
        {children}
      </LeafletMapContainer>
    </Box>
  )
}
